
import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsAtoDebt from 'Models/Security/Acl/VisitorsAtoDebt';
import UserAtoDebt from 'Models/Security/Acl/UserAtoDebt';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IAtoDebtEntityAttributes extends IModelAttributes {
	amount: number;
	recordAdded: Date;
	recordUpdated: Date;

	monitoredId: string;
	monitored: Models.MonitoredEntity | Models.IMonitoredEntityAttributes;

}

@entity('AtoDebtEntity', 'ATO Debt')
export default class AtoDebtEntity extends Model
	implements IAtoDebtEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsAtoDebt(),
		new UserAtoDebt(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	@Validators.Numeric()
	@observable
	@attribute<AtoDebtEntity, number>()
	@CRUD({
		name: 'Amount',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public amount: number;

	@observable
	@attribute<AtoDebtEntity, Date>()
	@CRUD({
		name: 'Record Added',
		displayType: 'datetimepicker',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public recordAdded: Date;

	@observable
	@attribute<AtoDebtEntity, Date>()
	@CRUD({
		name: 'Record Updated',
		displayType: 'datetimepicker',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public recordUpdated: Date;
	

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Monitored',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.MonitoredEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'monitoreds',
			oppositeEntity: () => Models.MonitoredEntity,
		}),
		
	})
	public monitoredId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public monitored: Models.MonitoredEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAtoDebtEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAtoDebtEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.amount !== undefined) {
				this.amount = attributes.amount;
			}
			if (attributes.recordAdded !== undefined) {
				if (attributes.recordAdded === null) {
					this.recordAdded = attributes.recordAdded;
				} else {
					this.recordAdded = moment(attributes.recordAdded).toDate();
				}
			}
			if (attributes.recordUpdated !== undefined) {
				if (attributes.recordUpdated === null) {
					this.recordUpdated = attributes.recordUpdated;
				} else {
					this.recordUpdated = moment(attributes.recordUpdated).toDate();
				}
			}
			if (attributes.monitoredId !== undefined) {
				this.monitoredId = attributes.monitoredId;
			}
			if (attributes.monitored !== undefined) {
				if (attributes.monitored === null) {
					this.monitored = attributes.monitored;
				} else if (attributes.monitored instanceof Models.MonitoredEntity) {
					this.monitored = attributes.monitored;
					this.monitoredId = attributes.monitored.id;
				} else {
					this.monitored = new Models.MonitoredEntity(attributes.monitored);
					this.monitoredId = this.monitored.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		monitored {
			${Models.MonitoredEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'monitored',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AtoDebtEntity.prototype, 'created');
CRUD(modifiedAttr)(AtoDebtEntity.prototype, 'modified');
